import { get, set } from 'lodash'
import { action, computed, makeObservable, observable } from 'mobx'
import dayjs from 'dayjs'

export class Budget {
	_class = 'Budget'
	budget_id = ''
	compact = {}
	full = {}
	to_delete = false
	modified = null
	loaded = -1

	/**
	 * @constructor Budget
	 * @description Initializes a new Budget instance with observables, computed values, and actions using MobX.
	 * @param {string} id - The unique budget ID used for identifying the instance.
	 */
	constructor(id) {
		this.budget_id = id
		this.compact = {}
		this.full = {}

		makeObservable(this, {
			budget_id: observable,
			compact: observable,
			full: observable,
			to_delete: observable,
			modified: observable,
			loaded: observable,

			// Computed properties for accessing data in compact/full
			name: computed,
			category_id: computed,
			budget_type: computed,
			period_start: computed,
			period_end: computed,
			value: computed,
			currency: computed,
			created_by: computed,
			assigned_user_id: computed,
			createdTs: computed,
			modifiedTs: computed,
			totalValue: computed,

			// Action methods for setting properties
			setName: action.bound,
			setCategory_id: action.bound,
			setBudgetType: action.bound,
			setPeriodStart: action.bound,
			setPeriodEnd: action.bound,
			setValue: action.bound,
			setCurrency: action.bound,
			setCreatedBy: action.bound,
			setAssignedUserId: action.bound,
			setToDelete: action.bound,
			setModified: action.bound,

			setNewFlag: action.bound,
			resetLastFullLoad: action.bound,
			updateFromServer: action
		})
	}

	// Computed properties with fallback to compact/full
	/**
	 * @function get name
	 * @description Retrieves the budget name, checking `full` first and falling back to `compact`.
	 * @returns {string}
	 */
	get name() {
		return get(this.full, 'name', get(this.compact, 'name', ''))
	}

	/**
	 * @function setName
	 * @description Sets the budget name in the `full` observable object.
	 * @param {string} value - The new name.
	 */
	setName(value) {
		this.chgInFull('name', value)
	}

	/**
	 * @function get category_id
	 * @description Retrieves the category ID for the budget.
	 * @returns {string}
	 */
	get category_id() {
		return get(
			this.full,
			'category_id',
			get(this.compact, 'category_id', '')
		)
	}

	/**
	 * @function setCategory_id
	 * @description Sets the category ID for the budget.
	 * @param {string} value - Category ID.
	 */
	setCategory_id(value) {
		this.chgInFull('category_id', value)
	}

	/**
	 * @function get budget_type
	 * @description Returns the budget type ('in' or 'out').
	 * @returns {string}
	 */
	get budget_type() {
		return get(
			this.full,
			'budget_type',
			get(this.compact, 'budget_type', 'in')
		)
	}

	/**
	 * @function setBudgetType
	 * @description Sets the budget type as 'in' or 'out'.
	 * @param {string} value - Either 'in' or 'out'.
	 */
	setBudgetType(value) {
		this.chgInFull('budget_type', value)
	}

	// Directly access `period_start` and `period_end` in `DATE` format (YYYY-MM-DD)
	/**
	 * @function get period_start
	 * @description Retrieves the start date of the budget period.
	 * @returns {string|null}
	 */
	get period_start() {
		return get(
			this.full,
			'period_start',
			get(this.compact, 'period_start', null)
		)
	}

	/**
	 * @function setPeriodStart
	 * @description Sets the start date of the budget period in YYYY-MM-DD format.
	 * @param {string|Date|dayjs.Dayjs|null} value - The new start date.
	 */
	setPeriodStart(value) {
		this.chgInFull(
			'period_start',
			value ? dayjs(value).format('YYYY-MM-DD') : null
		)
	}

	/**
	 * @function get period_end
	 * @description Retrieves the end date of the budget period.
	 * @returns {string|null}
	 */
	get period_end() {
		return get(
			this.full,
			'period_end',
			get(this.compact, 'period_end', null)
		)
	}

	/**
	 * @function setPeriodEnd
	 * @description Sets the end date of the budget period in YYYY-MM-DD format.
	 * @param {string|Date|dayjs.Dayjs|null} value - The new end date.
	 */
	setPeriodEnd(value) {
		this.chgInFull(
			'period_end',
			value ? dayjs(value).format('YYYY-MM-DD') : null
		)
	}

	/**
	 * @function get value
	 * @description Returns the numeric value of the budget.
	 * @returns {number}
	 */
	get value() {
		return get(this.full, 'value', get(this.compact, 'value', 0))
	}

	/**
	 * @function setValue
	 * @description Sets the numeric value of the budget.
	 * @param {number} value - The budget amount.
	 */
	setValue(value) {
		this.chgInFull('value', value)
	}

	/**
	 * @function get currency
	 * @description Retrieves the currency code used for this budget (e.g., 'usd').
	 * @returns {string}
	 */
	get currency() {
		return get(this.full, 'currency', get(this.compact, 'currency', 'usd'))
	}

	/**
	 * @function setCurrency
	 * @description Sets the currency code.
	 * @param {string} value - The currency code.
	 */
	setCurrency(value) {
		this.chgInFull('currency', value)
	}

	/**
	 * @function get created_by
	 * @description Returns the user ID of the budget creator.
	 * @returns {string}
	 */
	get created_by() {
		return get(this.full, 'created_by', get(this.compact, 'created_by', ''))
	}

	/**
	 * @function setCreatedBy
	 * @description Sets the creator user ID.
	 * @param {string} value - User ID.
	 */
	setCreatedBy(value) {
		this.chgInFull('created_by', value)
	}

	/**
	 * @function get assigned_user_id
	 * @description Returns the assigned user ID.
	 * @returns {string}
	 */
	get assigned_user_id() {
		return get(
			this.full,
			'assigned_user_id',
			get(this.compact, 'assigned_user_id', '')
		)
	}

	/**
	 * @function setAssignedUserId
	 * @description Sets the assigned user ID for the budget.
	 * @param {string} value - User ID.
	 */
	setAssignedUserId(value) {
		this.chgInFull('assigned_user_id', value)
	}

	/**
	 * @function get createdTs
	 * @description Computes the UNIX timestamp of the creation date.
	 * @returns {number|null}
	 */
	get createdTs() {
		const createdTs = get(
			this.full,
			'created_at',
			get(this.compact, 'created_at', null)
		)
		return createdTs ? dayjs(createdTs).unix() : null
	}

	/**
	 * @function get modifiedTs
	 * @description Computes the UNIX timestamp of the modified date.
	 * @returns {number|null}
	 */
	get modifiedTs() {
		return this.modified ? dayjs(this.modified).unix() : null
	}

	/**
	 * @function setModified
	 * @description Sets the modified timestamp value.
	 * @param {string|Date|null} value - The timestamp.
	 */
	setModified(value) {
		this.modified = value
	}

	/**
	 * @function setToDelete
	 * @description Sets the `to_delete` flag or timestamp.
	 * @param {boolean|number} value - Deletion flag or scheduled timestamp.
	 */
	setToDelete(value) {
		this.to_delete = value
	}

	/**
	 * @function get totalValue
	 * @description Returns the total value converted to the defined currency.
	 * @returns {number}
	 */
	get totalValue() {
		return this.convertCurrency(this.value, this.currency)
	}

	/**
	 * Converts the budget value based on the selected currency.
	 *
	 * @param {string} currency - The currency to convert to (usd, eur, ron).
	 * @returns {number} The converted value.
	 */
	convertCurrency(currency) {
		const conversionRates = {
			usd: { eur: 0.92, ron: 4.59 },
			eur: { usd: 1.18, ron: 5.29 },
			ron: { usd: 0.22, eur: 0.19 }
		}

		if (currency === 'usd') return parseFloat(this.value.toFixed(2))

		const rate = conversionRates['usd'][currency]
		return rate ? parseFloat((this.value * rate).toFixed(2)) : this.value
	}

	/**
	 * @function setNewFlag
	 * @description Marks this item as new by setting the loaded flag to -2.
	 */
	setNewFlag = () => {
		this.loaded = -2
	}

	/**
	 * @function resetLastFullLoad
	 * @description Updates the loaded flag to the current timestamp to track last load time.
	 */
	resetLastFullLoad = () => {
		this.loaded = dayjs().unix()
	}

	/**
	 * Updates the budget item with the data from the server.
	 * @param {object} srvJson - The server data.
	 */
	updateFromServer(srvJson) {
		if (typeof srvJson.compact === 'string') {
			try {
				this.compact = JSON.parse(srvJson.compact)
			} catch (error) {
				console.error('Failed to parse compact JSON:', error)
				this.compact = {}
			}
		} else {
			this.compact = srvJson.compact || {}
		}

		if (typeof srvJson.full === 'string') {
			try {
				this.full = JSON.parse(srvJson.full)
			} catch (error) {
				console.error('Failed to parse full JSON:', error)
				this.full = {}
			}
		} else {
			this.full = srvJson.full || {}
		}

		this.to_delete = srvJson.to_delete || false
		this.modified = srvJson.modified || null
	}

	/**
	 * Helper to update the full object with MobX observability.
	 * @param {string} path - Path within `full`
	 * @param {*} value - Value to set
	 */
	chgInFull(path, value) {
		if (get(this.full, path) !== value) {
			set(this.full, path, value)
		}
	}
}
